import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, endpoints } from '../../config/api';
import { Badge } from '../../components/ui';
import './OrganizationsPage.css';
import './DemoRequestsPage.css';

// Landing page "Demo planlayın" leads (exchange_setup.DemoRequest). Each new
// one also emails the team with reply-to set to the requester; this page is
// where they're tracked through to a demo.
const STATUSES = [
  ['new', 'Yeni', 'warning'],
  ['contacted', 'İletişime Geçildi', 'info'],
  ['scheduled', 'Demo Planlandı', 'success'],
  ['closed', 'Kapandı', 'purple'],
];
const STATUS_VARIANT = Object.fromEntries(STATUSES.map(([k, , v]) => [k, v]));

const DemoRequestsPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [notes, setNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(endpoints.admin.demoRequests.list, { params: filter ? { status: filter } : {} });
      setRows(data.results || []);
      setCounts(data.counts || {});
    } catch (err) {
      setError(err.response?.data?.error || 'Demo talepleri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id, patch) => {
    try {
      const updated = await api.patch(endpoints.admin.demoRequests.update(id), patch);
      setRows((rs) => rs.map((r) => (r.id === id ? updated : r)));
      if (patch.status) load();
    } catch (err) {
      setError(err.response?.data?.error || 'Güncellenemedi');
    }
  };

  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1>Demo Talepleri</h1>
          <p className="page-header-subtitle">Landing sayfasındaki “Demo planlayın” formundan gelen talepler</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={load}>↻ Yenile</button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>Kapat</button>
        </div>
      )}

      <div className="demo-req-tabs" role="tablist">
        <button role="tab" aria-selected={!filter} className={!filter ? 'is-on' : ''} onClick={() => setFilter('')}>
          Tümü <span>{total}</span>
        </button>
        {STATUSES.map(([key, label]) => (
          <button key={key} role="tab" aria-selected={filter === key} className={filter === key ? 'is-on' : ''} onClick={() => setFilter(key)}>
            {label} <span>{counts[key] || 0}</span>
          </button>
        ))}
      </div>

      <div className="table-container">
        {loading ? (
          <div className="loading-spinner"><div className="spinner"></div></div>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <h3>Talep yok</h3>
            <p>Bu filtrede henüz bir demo talebi bulunmuyor</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Şirket</th>
                <th>Kişi</th>
                <th>Çalışan</th>
                <th>Durum</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr className="clickable-row" onClick={() => setOpenId(openId === r.id ? null : r.id)}>
                    <td>{new Date(r.created_at).toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' })}</td>
                    <td><strong>{r.company}</strong></td>
                    <td>
                      <div className="demo-req-person">
                        <span>{r.full_name}</span>
                        <a href={`mailto:${r.email}`} onClick={(e) => e.stopPropagation()}>{r.email}</a>
                        {r.phone && <small>{r.phone}</small>}
                      </div>
                    </td>
                    <td>{r.company_size_display}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="demo-req-status">
                        <Badge variant={STATUS_VARIANT[r.status]}>{r.status_display}</Badge>
                        <select value={r.status} onChange={(e) => update(r.id, { status: e.target.value })} aria-label="Durumu değiştir">
                          {STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                      </div>
                    </td>
                  </tr>
                  {openId === r.id && (
                    <tr className="demo-req-detail">
                      <td colSpan={5}>
                        <div className="demo-req-detail-grid">
                          <div>
                            <h4>Talep notu</h4>
                            <p>{r.message || '—'}</p>
                            <small>KVKK onayı: {new Date(r.kvkk_consent_at).toLocaleString('tr-TR')}</small>
                            <div className="demo-req-convert">
                              <button
                                className="btn btn-secondary"
                                onClick={() => navigate('/admin/organizations', {
                                  state: { prefill: { name: r.company, admin_email: r.email, billing_email: r.email } },
                                })}
                              >
                                Müşteri hesabı aç →
                              </button>
                            </div>
                          </div>
                          <div>
                            <h4>Admin notu</h4>
                            <textarea
                              rows={3}
                              value={notes[r.id] ?? r.admin_notes}
                              onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                              placeholder="Görüşme notları, planlanan tarih…"
                            />
                            <button
                              className="btn btn-primary"
                              disabled={(notes[r.id] ?? r.admin_notes) === r.admin_notes}
                              onClick={() => update(r.id, { admin_notes: notes[r.id] })}
                            >
                              Notu kaydet
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default DemoRequestsPage;
